import './SaveProjectForm.scss';
import { useForm, useFormWatch } from '@form-ts/react';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { FormattedHTMLMessage, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useAction } from 'src/hooks/useAction';
import { Button } from 'src/modules/common/components/Button';
import { FormattedMathMessage } from 'src/modules/common/components/FormattedMathMessage';
import { TileCard } from 'src/modules/common/components/TileCard';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { InfoIcon } from 'src/modules/common/icons/InfoIcon';
import { LoadingIcon } from 'src/modules/common/icons/LoadingIcon';
import { LoadingSpinner } from 'src/modules/common/icons/LoadingSpinner';
import { MarkDoneIcon } from 'src/modules/common/icons/MarkDoneIcon';
import { TextField } from 'src/modules/form/components/TextField';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { getIsPending } from 'src/modules/save-project/getIsPending';
import { getIsSuccess } from 'src/modules/save-project/getIsSuccess';
import { SAVE_PROJECT } from 'src/modules/save-project/SaveProjectAction';
import { SAVE_PROJECT_SCHEMA } from 'src/modules/save-project/SavePrpjectFormSchema';

export const SaveProjectForm = (): ReactElement => {
  const [isSent, setIsSent] = useState(true);
  const intl = useIntl();
  const form = useForm<{ email: string }, FormError>('save-project', {
    initialValues: { email: '' },
    reinitialize: false,
  });

  const isPending = useSelector(getIsPending);
  const isSuccess = useSelector(getIsSuccess);

  useFormValidator(form, SAVE_PROJECT_SCHEMA);

  const saveProject = useAction(SAVE_PROJECT.request);

  const onClick = useCallback(() => {
    saveProject({ email: form.currentState.values.email });
    setIsSent(true);
  }, [form.currentState.values.email, saveProject, setIsSent]);

  const errors = useFormWatch(form, form.errors.get);

  useEffect(() => form.subscribe((prevState) => {
    const nextState = form.currentState;
    if (prevState.values !== nextState.values) {
      setIsSent(false);
    }
  }), [form]);

  return (
    <div className="bp-save-project-form">
      <TileCard
        title={<FormattedMessage id="saveProjectForm/title" values={{ sub, sup }}/>}
      >
        <div className="bp-save-project-form--legal">
          <p>
            <FormattedMessage id="saveProjectForm/legal"/>

          </p>
          <Tooltip
            id="bp-saveProjectForm-tooltip"
            value={(
              <FormattedHTMLMessage
                id="saveProjectForm/tooltip/content"
              />
            )}
          >
            <div
              className="bp-save-project-form__tooltip"
            >
              <InfoIcon/>
            </div>
          </Tooltip>
        </div>
        <TextField
          field={form.field.at('email')}
          label={intl.formatMessage({ id: 'admin/user/form/field/email' })}
          required={true}
          size="md"
          maxLength={100}
        />
        <Button
          size="md"
          disabled={isSent || Object.entries(errors).length > 0 || isPending}
          onClick={onClick}
          loading={isPending}
          icon={isSent && isSuccess ? <MarkDoneIcon/> : null}
        >
          <FormattedMessage id="saveProjectForm/button"/>
          <div className="bp-bottom-navigation-helper__button-icon">
            <ArrowRightIcon/>
          </div>
        </Button>
      </TileCard>
    </div>
  );
};
